import { OpenHolidaysSchoolHoliday, SchoolHoliday } from "./holidays";
import { TimeEntryType } from "./model";
import { dateAdd } from "./utils";

export type Locales = "DE" | "AT";
export type LocalesSettings = {
    states: { name: string; code: string }[];
    openHolidayStateCodes: Record<string, string>;
    timeEntryTypeLabel: (type: TimeEntryType) => string;
    timeEntryTypeIcon: (type: TimeEntryType) => string;
    mapOpenHoliday: (holiday: OpenHolidaysSchoolHoliday) => SchoolHoliday;
    maxWorkingHoursPerDay: number;
    minRestingHours: number;
};

const GermanLocale = {
    states: [
        { name: "Baden-Württemberg", code: "BW" },
        { name: "Bayern", code: "BY" },
        { name: "Augsburg, Bayern", code: "AB" },
        { name: "Berlin", code: "BE" },
        { name: "Brandenburg", code: "BB" },
        { name: "Bremen", code: "HB" },
        { name: "Hamburg", code: "HH" },
        { name: "Hessen", code: "HE" },
        { name: "Mecklenburg-Vorpommern", code: "MV" },
        { name: "Niedersachsen", code: "NI" },
        { name: "Nordrhein-Westfalen", code: "NW" },
        { name: "Rheinland-Pfalz", code: "RP" },
        { name: "Saarland", code: "SL" },
        { name: "Sachsen", code: "SN" },
        { name: "Sachsen-Anhalt", code: "ST" },
        { name: "Schleswig-Holstein", code: "SH" },
        { name: "Thüringen", code: "TH" },
    ],
    // OpenHolidays uses alternative state codes for Germany so we need to map them to the ISO state codes
    openHolidayStateCodes: {
        BW: "BW",
        BY: "BY",
        AB: "BY",
        BE: "BE",
        BB: "BB",
        HB: "HB",
        HH: "HH",
        HE: "HE",
        // MV splits school holidays into Allgemeinbildende Schulen (ABS) and Berufsbildende Schulen (BBS)
        // We only use the ABS holidays for now
        "MV-ABS": "MV",
        NI: "NI",
        NW: "NW",
        RP: "RP",
        SL: "SL",
        SN: "SN",
        ST: "ST",
        SH: "SH",
        TH: "TH",
    },
    maxWorkingHoursPerDay: 10,
    minRestingHours: 11,
    timeEntryTypeLabel(type: TimeEntryType): string {
        switch (type) {
            case TimeEntryType.Work:
                return "Arbeit";
            case TimeEntryType.Vacation:
                return "Urlaub";
            case TimeEntryType.Sick:
                return "Krank";
            case TimeEntryType.ChildSick:
                return "Kind Krank";
            case TimeEntryType.SickInKUG:
                return "Krank in KUG";
            case TimeEntryType.School:
                return "Schule";
            case TimeEntryType.HourAdjustment:
                return "Zeitbuchung";
            case TimeEntryType.VacationAdjustment:
                return "Urlaubsbuchung";
            case TimeEntryType.Free:
                return "Frei";
            case TimeEntryType.CompDay:
                return "Guttag";
            default:
                return "";
        }
    },
    timeEntryTypeIcon(type: TimeEntryType): string {
        switch (type) {
            case TimeEntryType.Work:
                return "digging";
            case TimeEntryType.Vacation:
                return "island-tropical";
            case TimeEntryType.Sick:
            case TimeEntryType.SickInKUG:
                return "face-thermometer";
            case TimeEntryType.ChildSick:
                return "baby";
            case TimeEntryType.HourAdjustment:
            case TimeEntryType.VacationAdjustment:
                return "plus-minus";
            case TimeEntryType.Free:
                return "couch";
            case TimeEntryType.CompDay:
                return "gift";
            default:
                return "";
        }
    },
    mapOpenHoliday(holiday: OpenHolidaysSchoolHoliday): SchoolHoliday {
        return {
            ..._getSchoolHolidayCoreData(holiday),
            states: holiday.nationwide
                ? GermanLocale.states.map((s) => s.code)
                : holiday.subdivisions?.map(
                      (s) =>
                          GermanLocale.openHolidayStateCodes[
                              s.shortName as keyof typeof GermanLocale.openHolidayStateCodes
                          ] as StateCode
                  ) || [],
        };
    },
} as const satisfies LocalesSettings;

const AustrianLocale = {
    states: [
        { name: "Burgenland", code: "AT-1" },
        { name: "Kärnten", code: "AT-2" },
        { name: "Niederösterreich", code: "AT-3" },
        { name: "Oberösterreich", code: "AT-4" },
        { name: "Salzburg", code: "AT-5" },
        { name: "Steiermark", code: "AT-6" },
        { name: "Tirol", code: "AT-7" },
        { name: "Vorarlberg", code: "AT-8" },
        { name: "Wien", code: "AT-9" },
    ],
    // OpenHolidays uses alternative state codes for Austria so we need to map them to the ISO state codes
    openHolidayStateCodes: {
        BL: "AT-1",
        KÄ: "AT-2",
        NÖ: "AT-3",
        OÖ: "AT-4",
        SB: "AT-5",
        SM: "AT-6",
        TI: "AT-7",
        VA: "AT-8",
        WI: "AT-9",
    },
    maxWorkingHoursPerDay: 12,
    minRestingHours: 11,
    timeEntryTypeLabel(type: TimeEntryType): string {
        switch (type) {
            case TimeEntryType.ChildSick:
                return "Pflegeurlaub";
            default:
                return GermanLocale.timeEntryTypeLabel(type);
        }
    },
    timeEntryTypeIcon(type: TimeEntryType): string {
        switch (type) {
            case TimeEntryType.ChildSick:
                return "hand-holding-medical";
            default:
                return GermanLocale.timeEntryTypeIcon(type);
        }
    },

    mapOpenHoliday(holiday: OpenHolidaysSchoolHoliday): SchoolHoliday {
        return {
            ..._getSchoolHolidayCoreData(holiday),
            states: holiday.nationwide
                ? AustrianLocale.states.map((s) => s.code)
                : holiday.subdivisions?.map(
                      (s) =>
                          AustrianLocale.openHolidayStateCodes[
                              s.shortName as keyof typeof AustrianLocale.openHolidayStateCodes
                          ] as StateCode
                  ) || [],
        };
    },
} as const satisfies LocalesSettings;

function _getSchoolHolidayCoreData(holiday: OpenHolidaysSchoolHoliday): SchoolHoliday {
    return {
        start: holiday.startDate,
        // OpenHolidays end days are inclusive, whereas in Pentacode end dates are exclusive
        end: dateAdd(holiday.endDate, { days: 1 }),
        name: holiday.name.find((n) => n.language === "DE")?.text || holiday.name[0].text || "Unbenannt",
    };
}

export const Localized = {
    DE: GermanLocale,
    AT: AustrianLocale,
} satisfies Record<Locales, LocalesSettings>;

export type StateCode = (typeof Localized)[Locales]["states"][number]["code"];
export type Country = keyof typeof Localized;
